import { useEffect, useState } from "react";
import {
  Button,
  Flex,
  Grid,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import moment, { Moment } from "moment";
import "moment/locale/pt-br";
import { FaCalendarAlt, FaChevronLeft, FaChevronRight } from "react-icons/fa";

interface DateRangeFilterProps {
  /** valor de início no formato YYYY-MM-DD (ou "") */
  start: string;
  /** valor de fim no formato YYYY-MM-DD (ou "") */
  end: string;
  /** dispara com as datas selecionadas (YYYY-MM-DD ou "") */
  onChange: (start: string, end: string) => void;
  accentColor?: string;
  placeholder?: string;
}

const FMT = "YYYY-MM-DD";
const DISPLAY = "DD/MM/YYYY";
const toDisplay = (v: string) => (v ? moment(v, FMT).format(DISPLAY) : "");
const WEEK = ["D", "S", "T", "Q", "Q", "S", "S"];

export default function DateRangeFilter({
  start,
  end,
  onChange,
  accentColor = "#0030a8",
  placeholder = "dd/mm/aaaa - dd/mm/aaaa",
}: DateRangeFilterProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [viewMonth, setViewMonth] = useState<Moment>(
    moment(start || undefined, start ? FMT : undefined)
  );
  const [startInput, setStartInput] = useState(toDisplay(start));
  const [endInput, setEndInput] = useState(toDisplay(end));

  // sincroniza os campos digitáveis quando os filtros mudam por fora (ex.: Limpar)
  useEffect(() => setStartInput(toDisplay(start)), [start]);
  useEffect(() => setEndInput(toDisplay(end)), [end]);

  const handleDayClick = (day: Moment) => {
    const iso = day.format(FMT);
    if (!start || (start && end)) {
      onChange(iso, ""); // inicia um novo intervalo
    } else if (moment(iso).isBefore(moment(start), "day")) {
      onChange(iso, start); // clicou antes do início: inverte
    } else {
      onChange(start, iso);
    }
  };

  const commitTyped = () => {
    const s = moment(startInput, DISPLAY, true);
    const e = moment(endInput, DISPLAY, true);
    onChange(
      s.isValid() ? s.format(FMT) : "",
      e.isValid() ? e.format(FMT) : ""
    );
  };

  // grid sempre começando no domingo (day(0) não depende do locale), casando com WEEK
  const startOfGrid = viewMonth.clone().startOf("month").day(0);
  const days: Moment[] = [];
  for (let i = 0; i < 42; i++) days.push(startOfGrid.clone().add(i, "day"));

  const inRange = (d: Moment) =>
    !!start &&
    !!end &&
    d.isSameOrAfter(moment(start), "day") &&
    d.isSameOrBefore(moment(end), "day");
  const isEdge = (d: Moment) =>
    (!!start && d.isSame(moment(start), "day")) ||
    (!!end && d.isSame(moment(end), "day"));

  const displayLabel = start
    ? `${toDisplay(start)}${end ? " - " + toDisplay(end) : " - ..."}`
    : "";

  return (
    <Popover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      placement="bottom-start"
      isLazy
    >
      <PopoverTrigger>
        <InputGroup size="sm">
          <Input
            size="sm"
            readOnly
            cursor="pointer"
            value={displayLabel}
            placeholder={placeholder}
            bg="gray.50"
            borderColor="gray.200"
            _hover={{ borderColor: accentColor }}
          />
          <InputRightElement pointerEvents="none">
            <FaCalendarAlt color={accentColor} size={13} />
          </InputRightElement>
        </InputGroup>
      </PopoverTrigger>
      <Portal>
        <PopoverContent w="290px" _focus={{ boxShadow: "md", outline: "none" }}>
          <PopoverArrow />
        <PopoverBody>
          <Flex gap={2} mb={3}>
            <Input
              size="sm"
              placeholder="Início"
              value={startInput}
              onChange={(e) => setStartInput(e.target.value)}
              onBlur={commitTyped}
              onKeyDown={(e) => e.key === "Enter" && commitTyped()}
            />
            <Input
              size="sm"
              placeholder="Fim"
              value={endInput}
              onChange={(e) => setEndInput(e.target.value)}
              onBlur={commitTyped}
              onKeyDown={(e) => e.key === "Enter" && commitTyped()}
            />
          </Flex>

          <Flex justify="space-between" align="center" mb={2}>
            <IconButton
              aria-label="Mês anterior"
              size="xs"
              variant="ghost"
              icon={<FaChevronLeft />}
              onClick={() => setViewMonth(viewMonth.clone().subtract(1, "month"))}
            />
            <Text fontSize="sm" fontWeight="bold" textTransform="capitalize">
              {viewMonth.format("MMMM YYYY")}
            </Text>
            <IconButton
              aria-label="Próximo mês"
              size="xs"
              variant="ghost"
              icon={<FaChevronRight />}
              onClick={() => setViewMonth(viewMonth.clone().add(1, "month"))}
            />
          </Flex>

          <Grid templateColumns="repeat(7, 1fr)" gap={1}>
            {WEEK.map((w, i) => (
              <Text
                key={`w${i}`}
                fontSize="xs"
                fontWeight="bold"
                color="gray.500"
                textAlign="center"
              >
                {w}
              </Text>
            ))}
            {days.map((d, i) => {
              const otherMonth = d.month() !== viewMonth.month();
              const edge = isEdge(d);
              const range = inRange(d);
              return (
                <Button
                  key={`d${i}`}
                  size="xs"
                  h="28px"
                  minW="28px"
                  p={0}
                  fontWeight={edge ? "bold" : "normal"}
                  color={edge ? "white" : otherMonth ? "gray.400" : "gray.700"}
                  bg={edge ? accentColor : range ? "blue.50" : "transparent"}
                  _hover={{ bg: edge ? accentColor : "blue.100" }}
                  onClick={() => handleDayClick(d)}
                >
                  {d.date()}
                </Button>
              );
            })}
          </Grid>

          <Flex justify="flex-end" mt={3} gap={2}>
            <Button
              size="xs"
              variant="ghost"
              onClick={() => onChange("", "")}
            >
              Limpar
            </Button>
            <Button
              size="xs"
              bg={accentColor}
              color="white"
              _hover={{ opacity: 0.9 }}
              onClick={onClose}
            >
              Aplicar
            </Button>
          </Flex>
        </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}
